import { ProductVariantFlag } from '@/components/_products/ProductVariantFlag'
import { AspectRatio } from '@/components/_ui/AspectRatio'
import { Button } from '@/components/_ui/Button'
import T, { useTranslate } from '@/components/Helpers/Translate'
import { useModals } from '@/components/ModalManager/useModals'
import { ProductDetails } from '@/components/ProductDetails'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { useGetCustomerProductPricing } from '@/lib/hooks/helpers/useGetCustomerProductPricing'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import { getTerms } from '@/lib/hooks/services/useGetTerms'
import { generateProductEventData } from '@/lib/services/mixpanel/helpers/generateCartEventData'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { cn } from '@/lib/utils'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useLoadProductOverwrittenTerms } from '../../../../lib/hooks/helpers/useGetOverwriteTerms'
import PlusCircle from '../../_components/plusCircle'

type Props = {
  product: CosmicProductType
  cardIndex: number
}

export const ProductsGridCard = ({ product, cardIndex }: Props) => {
  const {
    setApp,
    user: { customerType },
    app: { marketExceptions, isSearchBarActive },
  } = useGlobalStore()

  const { modals } = useModals()

  const { getAssociatePricingData } = useLoadProductOverwrittenTerms()
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const { gtm } = useGTM()

  const [showFlavorList, setShowFlavorList] = useState(false)
  const [indexSelected, setIndexSelected] = useState(0)

  const { translate } = useTranslate()

  const isAssociate = customerType === 'Associate'
  const isCustomer = customerType === 'Customer'
  const memberPricing = customerType !== 'Customer'

  // Optional chaining on this variable is needed to prevent application from crashing
  // when product variation is changed and then sorting is applied
  const productVariationSelected = product.variations[indexSelected]

  const { partsLabel } = productVariationSelected ?? {}

  const { displayPrice } = useGetCustomerProductPricing()

  // Uncomment when we get blurred feat back
  // const imageUrl = `${productVariationSelected?.variation.value?.data.carousel?.[0].carouselImage}?height=10&width=10&quality=1&format=webp`
  // const { blurContext } = useBlurContext(imageUrl)

  const correctPricing =
    isAssociate && productVariationSelected?.pricing !== null
      ? getAssociatePricingData(productVariationSelected?.pricing)
      : productVariationSelected?.pricing
  const selectedPv = getTerms({
    forCustomerType: customerType,
    pricingData: correctPricing,
  })?.pvEach

  useEffect(() => {
    const handleResize = () => {
      setShowFlavorList(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const productPartsData = () => {
    if (partsLabel) {
      return {
        content: <span>{partsLabel}</span>,
        hasPartsData: true,
      }
    }

    return {
      content: null,
      hasPartsData: false,
    }
  }

  const productGridCardDescription = () => (
    <div
      className={`flex flex-auto flex-col rounded-b-xl bg-white p-2 sm:p-5 ${
        showFlavorList && product.variations.length > 1 ? 'pt-1' : 'pt-4'
      }`}
    >
      <p
        className={`mb-2 text-base font-semibold leading-tight sm:text-lg ${
          showFlavorList ? 'underline' : ''
        }`}
      >
        {product.name}
      </p>
      <div className="flex w-full items-end justify-between">
        <div className="flex flex-col gap-y-1 text-xs leading-4 sm:text-sm">
          <p className="flex flex-col gap-1">
            <div className="flex flex-col gap-1 ">
              {isAssociate && (
                <>
                  <span>
                    {translate('pv')}: {selectedPv}
                  </span>
                </>
              )}

              {productPartsData().content}
            </div>

            {isCustomer && (
              <div className="flex">
                {productPartsData().hasPartsData && (
                  <span className="mx-1 hidden">|</span>
                )}

                <span className="mr-1 font-semibold">
                  <T>price</T>:{' '}
                </span>

                <span>
                  {displayPrice({
                    forCustomerType: customerType,
                    pricingData: productVariationSelected?.pricing,
                  })}
                </span>
              </div>
            )}
          </p>

          {memberPricing && (
            <div className="flex flex-col gap-1">
              <div>
                <span className="font-semibold text-[#398485]">
                  <T>{customerType.toLowerCase()}</T>:{' '}
                </span>
              </div>

              <div className="space-x-2">
                <span className="font-bold text-[#398485] ">
                  {displayPrice({
                    forCustomerType: customerType,
                    pricingData: productVariationSelected?.pricing,
                  })}
                </span>
                <span className={cn(memberPricing && 'line-through')}>
                  {displayPrice({
                    forCustomerType: 'Customer',
                    pricingData: productVariationSelected?.pricing,
                  })}
                </span>
              </div>
            </div>
          )}
          <span className="text-gray-500">
            {marketExceptions?.tax?.inclusive && <T>vat_included</T>}
          </span>
        </div>

        <div className="pointer-events-none">
          <Button
            style={{
              zIndex: 100,
            }}
            type="button"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()

              modals.openModal({
                type: 'dialogOrDrawer',
                modalId: 'quick-buy-modal',

                modal: {
                  children: (
                    <div className="h-full max-h-[90vh] overflow-auto p-5 pb-40 pt-0 lg:p-0">
                      <ProductDetails productData={product} isQuickAdd={true} />
                    </div>
                  ),
                },
              })
            }}
            variant="icon"
            className="pointer-events-auto"
          >
            <Image
              src="/icons/cart-quick-buy.svg"
              alt="add"
              width={20}
              height={20}
              className="h-[20px] min-h-[20px] w-[20px] min-w-[20px]"
            />
          </Button>
        </div>
      </div>
    </div>
  )

  const handleMouseEnterCard = () => {
    setShowFlavorList(true)
  }

  const handleMouseLeaveCard = () => {
    setShowFlavorList(false)
  }

  const handleMouseEnterFlavor = (index: number) => {
    setIndexSelected(index)
  }

  const getProductPath = () => {
    let url = `/product/${product.slug}`

    const sku = productVariationSelected?.sku

    const queryParams = new URLSearchParams({
      sku: sku ?? '',
    })

    // Add query params if there are multiple variations
    if (product.variations.length > 1) url += `?${queryParams}`

    return url
  }

  const renderVariation = (variation: CosmicVariationType, index: number) => {
    const findColorInAttributes = (flavorName: string): string | undefined => {
      for (const attribute of product.attributes) {
        const matchingOption = attribute.options.find(
          (opt) => opt.name === flavorName.trim(),
        )
        if (matchingOption?.style.backgroundColor) {
          return matchingOption.style.backgroundColor
        }
      }
      return undefined
    }

    const getVariationColorsWhenMultipleWithCosmicException = () => {
      const flavors = variation.combinationName.split('/')
      if (flavors.length <= 1) return null

      const colors = flavors
        .map((element) => findColorInAttributes(element))
        .filter(Boolean)
      if (colors.length <= 1) return colors[0]

      const gradientStops = colors.map((color) => {
        const percentage = 100 / colors.length
        return `${color} ${percentage}%`
      })

      return `linear-gradient(90deg, ${gradientStops.join(', ')})`
    }

    const getVariationColorWhenSingleColor = () =>
      findColorInAttributes(variation.combinationName)

    return (
      <div
        className={cn(
          `mr-2 h-5 w-5 rounded-full ${
            index === indexSelected ? 'border-2 border-[#6996C6]' : ''
          } `,
        )}
        // TODO: Convert to TailwindCSS
        style={{
          background:
            getVariationColorsWhenMultipleWithCosmicException() ??
            getVariationColorWhenSingleColor() ??
            undefined,
        }}
        key={index}
        onMouseEnter={() => handleMouseEnterFlavor(index)}
      />
    )
  }

  const renderProductImage = () => {
    if (!productVariationSelected.media?.[0]?.url) {
      return null
    }

    return (
      <Image
        src={productVariationSelected.media[0].url ?? ''}
        alt={productVariationSelected.media?.[0].altText ?? ''}
        // replace to blurContext when we get this feat back
        blurDataURL={
          'data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnID48ZmlsdGVyIGlkPSdiJyBjb2xvci1pbnRlcnBvbGF0aW9uLWZpbHRlcnM9J3NSR0InPjxmZUdhdXNzaWFuQmx1ciBzdGREZXZpYXRpb249JzIwJy8+PGZlQ29sb3JNYXRyaXggdmFsdWVzPScxIDAgMCAwIDAgMCAxIDAgMCAwIDAgMCAxIDAgMCAwIDAgMCAxMDAgLTEnIHJlc3VsdD0ncycvPjxmZUZsb29kIHg9JzAnIHk9JzAnIHdpZHRoPScxMDAlJyBoZWlnaHQ9JzEwMCUnLz48ZmVDb21wb3NpdGUgb3BlcmF0b3I9J291dCcgaW49J3MnLz48ZmVDb21wb3NpdGUgaW4yPSdTb3VyY2VHcmFwaGljJy8+PGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0nMjAnLz48L2ZpbHRlcj48aW1hZ2Ugd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgeD0nMCcgeT0nMCcgcHJlc2VydmVBc3BlY3RSYXRpbz0nbm9uZScgc3R5bGU9J2ZpbHRlcjogdXJsKCNiKTsnIGhyZWY9J2RhdGE6aW1hZ2Uvd2VicDtiYXNlNjQsVWtsR1JsZ0FBQUJYUlVKUVZsQTRXQW9BQUFBUUFBQUFDUUFBQ1FBQVFVeFFTQk1BQUFBQkQvRDgvNGlJSUJBZzhQOXRJYUwvYWF3QkFGWlFPQ0FlQUFBQU1BRUFuUUVxQ2dBS0FBekF6aVVBQUc0QUFQN3FyNnpUQk1qdGVIQUEnLz48L3N2Zz4='
        }
        placeholder="blur"
        fill={true}
        className="w-full rounded-xl object-contain"
      />
    )
  }

  if (product.variations.length === 0 && !productVariationSelected) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-center text-sm text-gray-500">
          {translate('no_variations')}
          <br />
          {product.slug}
        </p>
      </div>
    )
  }

  return (
    <a
      onClick={() => {
        setApp({ isSearchBarActive: false })

        gtm.selectItem({
          product,
        })

        mixpanelService
          .trackEvent(MixpanelEvent.SELECT_PRODUCT, {
            event_loction: isSearchBarActive
              ? 'search'
              : window.location.href.split('/').at(-1) || '',
            cart: generateProductEventData(
              product,
              productVariationSelected,
              marketExceptions?.currencyCode || 'USD',
              getTerms({
                forCustomerType: customerType,
                pricingData: correctPricing,
              })?.priceEach || 0,
              { index: cardIndex },
            ),
          })
          .catch(() => {})
      }}
      href={parseLinkUrlWithLocale({
        url: getProductPath(),
      })}
      className="relative mt-1 flex flex-col no-underline hover:cursor-pointer sm:mt-5 "
      onMouseEnter={handleMouseEnterCard}
      onMouseLeave={handleMouseLeaveCard}
      data-test={`product-${product.slug}`}
    >
      <AspectRatio ratio={1} className="relative rounded-t-xl bg-[#F7F7F7]">
        {productVariationSelected.flag?.label && (
          <ProductVariantFlag
            text={productVariationSelected.flag.label ?? ''}
            textColor={productVariationSelected.flag.style?.textColor ?? ''}
            backgroundColor={
              productVariationSelected?.flag.style?.backgroundColor ?? ''
            }
            className="absolute -top-2 right-3"
          />
        )}

        {renderProductImage()}
        {showFlavorList && product.variations.length > 1 && (
          <div className="absolute bottom-0 left-0 z-10 h-auto min-h-16 w-full bg-transparent px-4 pt-9 animate-in slide-in-from-bottom">
            <div className="flex justify-between">
              <div className="hidden md:flex">
                {product.variations
                  .slice(0, 5)
                  .map((variation, index) => renderVariation(variation, index))}
                {product.variations.length > 5 && <PlusCircle />}
              </div>
              <div className="flex md:hidden">
                {product.variations
                  .slice(0, 4)
                  .map((variation, index) => renderVariation(variation, index))}
                {product.variations.length > 5 && <PlusCircle />}
              </div>
              <span className="hidden underline opacity-80 md:block">
                {`${product?.variations.length}`} <T>variations</T>
              </span>
            </div>
          </div>
        )}
      </AspectRatio>
      {productGridCardDescription()}
    </a>
  )
}
